import React, { useRef, useState, useContext, createContext } from 'react';
import type { FC, ReactNode } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Button, ListItem, Popover, useMediaQuery } from '@mui/material';
import type { ListItemProps } from '@mui/material';
import ChevronDownIcon from '../res/icons/ChevronDown';
import ChevronRightIcon from '../res/icons/ChevronRight';
import i18n from 'i18next';
import { useTheme } from '@mui/material/styles';

// Context per chiudere il popover del ramo più vicino
const CloseMenusContext = createContext<() => void>(() => {});

interface NavItemProps extends ListItemProps {
    active?: boolean;
    children?: ReactNode;
    depth: number;
    icon?: ReactNode;
    info?: ReactNode;
    open?: boolean;
    path?: string;
    title: string;
    onClick?: () => void;
}

const NavItem: FC<NavItemProps> = (props) => {
    const {
        active = false,
        children,
        depth,
        icon,
        info,
        open: openProp = false,
        path,
        title,
        onClick,
        ...other
    } = props;

    const theme = useTheme();
    const [open, setOpen] = useState<boolean>(openProp);
    const anchorRef = useRef<HTMLButtonElement | null>(null);
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'), { defaultMatches: true });
    const closeMenusFromContext = useContext(CloseMenusContext);

    const handleToggle = (): void => {
        setOpen((prevOpen) => !prevOpen);
    };

    const closeOwnPopover = (): void => {
        setOpen(false);
    };

    // ✅ Padding: 16px solo per root (depth 0), 0px per sotto-item
    const paddingLeft = depth === 0 ? 16 : 0;

    // Branch (ha children)
    if (children) {
        return (
            <ListItem disableGutters sx={{ display: 'block', py: 0 }} {...other}>
                <Button
                    endIcon={!open ? <ChevronRightIcon fontSize="small" /> : <ChevronDownIcon fontSize="small" />}
                    onClick={handleToggle}
                    startIcon={icon}
                    ref={anchorRef}
                    sx={{
                        color: active ? 'text.secondary' : undefined,
                        fontWeight: 'fontWeightMedium',
                        justifyContent: 'flex-start',
                        pl: `${paddingLeft}px`,
                        pr: '8px',
                        py: '12px',
                        textAlign: 'left',
                        textTransform: 'none'
                    }}
                    variant="text"
                >
                    <Box sx={{ flexGrow: 1 }}>{title}</Box>
                    {info}
                </Button>

                <Popover
                    anchorEl={anchorRef.current}
                    anchorOrigin={
                        !isMobile
                            ? { vertical: 'bottom', horizontal: 'center' }
                            : { vertical: 'center', horizontal: 'right' }
                    }
                    transformOrigin={
                        !isMobile
                            ? { vertical: 'top', horizontal: 'center' }
                            : { vertical: 'top', horizontal: 'center' }
                    }
                    keepMounted
                    onClose={closeOwnPopover}
                    open={open}
                    PaperProps={{}}
                >
                    <Box sx={{ pt: 2, pl: 2, pr: 2, pb: 2 }}>
                        <CloseMenusContext.Provider value={closeOwnPopover}>
                            {children}
                        </CloseMenusContext.Provider>
                    </Box>
                </Popover>
            </ListItem>
        );
    }

    // Leaf (nessun children)

    const handleLeafClick = (): void => {
        closeMenusFromContext?.();
        onClick?.();
    };

    return (
        <ListItem disableGutters sx={{ display: 'flex', py: 0 }}>
            <Button
                component={path ? RouterLink : 'button'}
                startIcon={icon}
                sx={{
                    color: 'text.primary',
                    fontWeight: 'fontWeightMedium',
                    justifyContent: 'center',
                    textAlign: 'center',
                    pl: `${paddingLeft}px`,
                    pr: '8px',
                    textTransform: 'none',
                    '&.MuiButtonBase-root:hover': {
                        bgcolor: 'transparent',
                        color: 'secondary.main'
                    },
                    ...(active && {
                        color: 'secondary.main',
                        fontWeight: 'fontWeightBold',
                        '& svg': { color: 'secondary.main' }
                    })
                }}
                variant="text"
                to={path || undefined}
                onClick={handleLeafClick}
            >
                <Box sx={{ flexGrow: 1 }}>{title}</Box>
                {info}
            </Button>
        </ListItem>
    );
};

NavItem.propTypes = {
    active: PropTypes.bool,
    children: PropTypes.node,
    depth: PropTypes.number.isRequired,
    icon: PropTypes.node,
    info: PropTypes.node,
    open: PropTypes.bool,
    path: PropTypes.string,
    title: PropTypes.string.isRequired,
    onClick: PropTypes.func
};

NavItem.defaultProps = {
    active: false,
    open: false
};

export default NavItem;
